import { Theme } from '@mui/material'

import makeStyles from '@mui/styles/makeStyles'

const bp = 'sm'

type StylesType = {
  timelineMode?: boolean
  task?: boolean
  multiSelectTimelineVariant?: boolean
  changeAlignment?: boolean
  readOnly?: boolean
  canEdit?: boolean
  dateTimelineMode?: boolean
  smallMedia?: boolean
  inputOnly?: boolean
  inTable?: boolean
}

const useStyles = makeStyles<Theme, StylesType>(theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',

    [theme.breakpoints.down('md')]: {
      fontSize: theme.size.sm
    }
  },

  alignBaseline: {
    alignSelf: 'baseline'
  },

  fieldInputRoot: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: ({ timelineMode }) => timelineMode && 'unset !important',
    minHeight: ({ timelineMode }) => timelineMode && '35px !important',

    [theme.breakpoints.down('md')]: {
      marginTop: ({ task, inTable }) =>
        task && !inTable ? '2.5rem' : 'inherit'
    }
  },

  dropdownRoot: {
    '& div:nth-child(2)': {
      marginLeft: '2rem'
    },
    height: ({ multiSelectTimelineVariant }) =>
      multiSelectTimelineVariant && 'auto !important',
    minHeight: ({ multiSelectTimelineVariant }) =>
      multiSelectTimelineVariant && '35px',
    '& > div:first-child': {
      alignSelf: ({ multiSelectTimelineVariant, changeAlignment }) =>
        multiSelectTimelineVariant && changeAlignment && 'baseline'
    }
  },

  input: {
    flexGrow: 1,
    outline: 'none',
    border: 'none',
    marginLeft: ({ inputOnly }) => (inputOnly ? '0' : '2rem'),
    fontSize: theme.size.sm,
    padding: 0,
    width: '100%',
    background: 'transparent',
    resize: 'none',
    minWidth: ({ timelineMode }) => timelineMode && 80,
    cursor: ({ readOnly, canEdit }) =>
      canEdit ? (readOnly ? 'not-allowed' : 'auto') : 'default',

    [theme.breakpoints.down(bp)]: {
      margin: '0 0 0 2rem',
      width: ({ readOnly }) => (readOnly ? 'unset' : '100%'),
      fontSize: theme.size.sm,
      color: theme.palette.textLight
    }
  },

  multilineInput: {
    marginBottom: '9px',
    marginTop: '9px'
  },

  date: {
    flexGrow: 1,
    color: 'black',
    marginLeft: ({ inputOnly }) => (inputOnly ? '0' : '2rem'),
    fontSize: theme.size.sm,
    cursor: ({ readOnly, canEdit }) =>
      canEdit ? (readOnly ? 'not-allowed' : 'pointer') : 'default',
    whiteSpace: ({ dateTimelineMode }) =>
      dateTimelineMode ? 'nowrap' : 'unset',
    overflow: ({ dateTimelineMode }) => (dateTimelineMode ? 'hidden' : 'unset'),
    textOverflow: ({ dateTimelineMode }) =>
      dateTimelineMode ? 'ellipsis' : 'unset',
    maxWidth: ({ dateTimelineMode }) =>
      dateTimelineMode ? 'calc(137px - 2rem)' : 'unset'
  },

  inputTimezone: {
    // @ts-ignore
    flexGrow: 'unset',
    cursor: 'pointer !important',

    [theme.breakpoints.down(bp)]: {
      margin: '0',
      fontSize: theme.size.sm,
      color: theme.palette.textLight
    }
  },

  grid: {
    margin: '0.5rem 0',
    width: '100%',
    columnCount: 2,

    [theme.breakpoints.down(bp)]: {
      columnCount: 1
    }
  },

  gridItem: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '35px',
    margin: '0.25rem 0'
  },

  column: {
    color: 'grey',
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '50%',
    flex: '1',
    padding: ({ readOnly, timelineMode }) =>
      readOnly || timelineMode ? '0 !important' : '1.5rem 0',
    fontSize: ({ readOnly }) => (readOnly ? theme.size.sm : 'inherit'),

    '& > div': {
      fontSize: theme.size.sm,
      height: '35px',
      display: ({ readOnly }) => (readOnly ? 'flex' : 'inherit'),
      alignItems: 'center',

      [theme.breakpoints.down(bp)]: {
        height: ({ readOnly }) => (readOnly ? '35px' : 'unset'),
        flexDirection: ({ readOnly }) => (readOnly ? 'row' : 'column'),
        alignItems: ({ readOnly }) => (readOnly ? 'center' : 'flex-start'),
        justifyContent: ({ readOnly }) => (readOnly ? 'flex-start' : 'center'),
        paddingTop: ({ readOnly, smallMedia }) =>
          readOnly ? '0' : smallMedia ? '1rem' : '2rem'
      }
    },

    [theme.breakpoints.down(bp)]: {
      flex: '1',
      flexBasis: '100%',
      padding: '0 !important'
    }
  },

  label: {
    width: 235,
    flexShrink: 0,
    cursor: 'default',
    alignItems: 'center',
    fontSize: theme.size.sm,
    color: theme.palette.textLight
  },

  multilineLabel: {
    marginTop: '9px'
  },

  icon: {
    marginRight: '5px'
  },

  title: {
    flexBasis: '100%',
    alignItems: 'center',
    paddingTop: '1.5rem',
    color: '#323436',
    fontSize: theme.size.sm,
    display: ({ readOnly }) => (readOnly ? 'none' : 'flex'),

    [theme.breakpoints.down('lg')]: {
      paddingTop: '0',
      paddingBottom: '0.5rem'
    }
  },

  select: {
    marginLeft: ({ inputOnly }) => (inputOnly ? '0' : '2rem'),
    cursor: ({ readOnly, canEdit }) =>
      canEdit
        ? readOnly
          ? 'not-allowed !important'
          : 'auto'
        : 'default !important',
    // background of select box
    '& > div': {
      backgroundColor: 'transparent',
      paddingLeft: '0'
    },

    '& .MuiSelect-select.Mui-disabled': {
      cursor: ({ readOnly, canEdit }) =>
        canEdit ? (readOnly ? 'not-allowed !important' : 'auto') : 'default'
    }
  },

  calendar: {
    display: 'flex'
  },

  calendarSelect: {
    marginLeft: '2rem',
    color: 'black',
    cursor: ({ readOnly, canEdit }) =>
      canEdit ? (readOnly ? 'not-allowed' : 'pointer') : 'default'
  },

  checkbox: {
    '& .MuiCheckbox-root': {
      padding: '0px'
    },

    '& .MuiCheckbox-colorSecondary': {
      color: ({ inputOnly }) => (inputOnly ? 'grey' : '#bbbbbc')
    },

    '& .MuiCheckbox-colorSecondary.Mui-checked': {
      color: 'rgb(19, 151, 220, 0.8)'
    },

    '@media (max-width: 575px)': {
      maxHeight: 250
    },

    '& > div:nth-child(2)': {
      marginLeft: '2rem'
    }
  },

  avatar: {
    cursor: 'pointer',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    paddingRight: 'calc(0.5rem + 16px)',

    '&:hover .close': {
      display: 'block'
    },

    '& .avatar': {
      position: 'absolute',
      top: '50%',
      left: '0',
      width: '24px',
      height: '24px',
      transform: 'translateY(-50%)',
      fontSize: theme.size.sm
    },

    '& .name': {
      marginLeft: 'calc(24px + 0.5rem)'
    },

    '& .close': {
      display: 'none',
      position: 'absolute',
      top: '50%',
      right: '0',
      marginLeft: '0.5rem',
      borderRadius: '50%',
      width: theme.size.base,
      height: theme.size.base,
      color: 'white',
      background: 'grey',
      transform: 'translateY(-50%)'
    }
  },

  avatarNone: {
    cursor: 'pointer',
    // @ts-ignore
    color: ({ inputOnly }) => (inputOnly ? 'black' : theme.palette.textLight),
    fontSize: theme.size.sm
  },

  noInfo: {
    color: '#657786',
    fontSize: theme.size.sm
  },

  noTags: {
    marginLeft: '2rem'
  },

  arrow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontSize: theme.size.sm
  },

  error: {
    marginLeft: '1rem',
    color: 'red',
    [theme.breakpoints.down(bp)]: {
      marginLeft: '0'
    }
  },

  dropDownValue: {
    color: 'black'
  },

  multiDropdownItem: {
    margin: ({ multiSelectTimelineVariant }) =>
      multiSelectTimelineVariant
        ? '0.0625rem 0.25rem 0.0625rem 0'
        : '0 0.25rem',
    padding: '0.25rem 0.5rem',
    backgroundColor: '#F1F1F1',
    color: 'grey',
    borderRadius: '0.75rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    '&:first-child': {
      marginLeft: ({ multiSelectTimelineVariant }) =>
        !multiSelectTimelineVariant && '0px'
    }
  },

  multiDropdownItemContainer: {
    display: 'flex',
    flexWrap: 'wrap'
  },

  glowRoot: {
    width: '99%',
    animation: '$glow 0.5s linear infinite alternate'
  },

  '@keyframes glow': {
    '0%': {
      boxShadow: `0 4px 10px 0 ${theme.palette.primary.main}`
    },

    '100%': {
      backgroundColor: `0 4px 10px 0 ${theme.palette.secondary.main}`
    }
  }
}))

export default useStyles
